import React from 'react';
import * as XLSX from 'xlsx';
import { ColInfo, ExcelDataType, CellObject } from 'xlsx/types';
import { DocumentNode, useLazyQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import moment from 'moment';

export declare type HeaderType = 'string' | 'date' | 'number' | 'time';
export declare type FormatType =
  | 'int'
  | 'float'
  | 'percent'
  | 'percentfloat'
  | 'datetime';

export declare type Header = {
  data: string;
  type?: HeaderType;
  format?: FormatType;
  refKey?: string; // Relative data bject key
};

const TIMESTAMP_FORMAT = 'YYYYMMDD.HHmmss';

interface ForceColInfo extends ColInfo {
  /** width in "characters" */
  wch: number;
}

interface WorkSheet extends XLSX.WorkSheet {
  '!cols': ForceColInfo[];
}

interface DataType {
  type: ExcelDataType;
  format?: string;
}

interface PropsExportData {
  filename?: string;
  sheetName?: string;
}

interface PropsExportQueryData<T extends Record<string, unknown>>
  extends PropsExportData {
  query: DocumentNode;
  variables: Record<string, unknown>;
  parseData: (data: T) => Array<Record<string, string | number | null>>;
}

type ExportData = (input: {
  type: string;
  headers?: Array<Header>;
  data: Array<Record<string, string | number | null>>;
}) => void;

/**
 * Runs the query and writes the file.
 *
 * @returns how many rows were written, so the caller can react to an export
 *          that produced nothing, or `null` when the query itself failed —
 *          the hook already reports that case, and a `0` there would read as
 *          "the query worked and found nothing", which is a different problem.
 */
type ExportQueryData = (
  type: string,
  headers?: Array<Header>,
) => Promise<number | null>;

type HookExportData = (props: PropsExportData) => { exportData: ExportData };

type HookExportQueryData = <T extends Record<string, unknown>>(
  props: PropsExportQueryData<T>,
) => {
  exportData: ExportData;
  exportQueryData: ExportQueryData;
  loading: boolean;
};

interface ExporterArgs {
  filename: string;
  headers?: Array<Header>;
  data: Array<Record<string, string | number | null>>;
  outputType?: 'xlsx' | 'csv';
}

const getType = (currentType: HeaderType | undefined): DataType => {
  switch (currentType) {
    case 'time':
      return { format: '[h]:mm:ss', type: 's' };
    case 'date':
      return { format: 'm/d/yy h:mm', type: 'd' };

    case 'number':
      return { type: 'n' };

    default:
      // string
      break;
  }
  return { type: 's' };
};

/**
 * Converts a 1-based column index into its corresponding Excel-style column name.
 *
 * For example:
 * - 1 → "A"
 * - 26 → "Z"
 * - 27 → "AA"
 * - 52 → "AZ"
 * - 703 → "AAA"
 *
 * This function is useful when determining the range of cells (e.g., "A1:ZZ100")
 * for dynamically generated Excel worksheets.
 *
 * @param {number} maxColNumber - The 1-based index of the last column (e.g., 1 for "A", 28 for "AB").
 * @returns {string} The Excel-style column label corresponding to the given index.
 */
const getMaxColRange = (maxColNumber: number) => {
  let colNumber = maxColNumber;
  let colName = '';

  /** The capital A char UTF-16 code unit */
  const A_CHAR_INDEX = 65;
  /** The number of chars in the English alphabet */
  const ALPHABET_CHAR_COUNT = 26;

  while (colNumber > 0) {
    colName =
      String.fromCharCode(
        A_CHAR_INDEX + ((colNumber - 1) % ALPHABET_CHAR_COUNT),
      ) + colName;
    colNumber = Math.floor((colNumber - 1) / ALPHABET_CHAR_COUNT);
  }

  return colName;
};

/**
 * Exports XLS data
 */
export const xlsExporter = ({
  filename,
  sheetName,
  headers = [],
  data,
  outputType = 'xlsx',
}: ExporterArgs & { sheetName?: string }) => {
  if (!data.length) {
    return;
  }

  const wb: XLSX.WorkBook = XLSX.utils.book_new();

  if (!wb.Props) {
    wb.Props = {
      Title: filename,
      Author: 'Quality24',
    };
  }

  wb.SheetNames.push(sheetName || 'Sheet');

  const ws: WorkSheet = {
    // Force creation of empty worksheet
    ...XLSX.utils.aoa_to_sheet([[]]),
    ...{ '!cols': [] }, // Initialize columns width
  };

  const headerIndex: Record<string, number> = {};

  // Build header
  (headers || []).forEach((column, colNumber) => {
    if (column.refKey) headerIndex[column.refKey] = colNumber;
    const cellRef = XLSX.utils.encode_cell({
      c: colNumber,
      r: 0,
    });
    ws['!cols'].push({ wch: column.data.length });
    ws[cellRef] = {
      v: column.data,
      t: 's',
    } as CellObject;
  });

  let maxColNumber = 0;
  let colNumber = 0;
  // Build each line
  data.forEach((line, lineNumber) => {
    colNumber = 0;
    // Buil each cell
    Object.entries(line).forEach(([colName, value]) => {
      if (colName in headerIndex) colNumber = headerIndex[colName];

      const cellRef = XLSX.utils.encode_cell({
        c: colNumber,
        r: lineNumber + 1,
      });

      const dataType: DataType = headers
        ? getType(headers[colNumber].type)
        : { type: 's' };
      ws[cellRef] = {
        v: value ?? '',
        /** When using DVC, all operations, including the mapping step, occurs before this step, so any value should get here already mapped. If we try to create a cell with a given format, like 'd' or 'n', passing an invalid value, this will generate a 'NaN'output, corrupting the worksheet.  */
        t:
          (dataType.type === 'd' || dataType.type === 'n') &&
          (value === '' || value === '-' || value === 'null')
            ? { type: 's' }
            : dataType.type,
      };

      if (headers && headers[colNumber].format) {
        switch (headers[colNumber].format) {
          case 'int':
            ws[cellRef].z = '0';
            break;
          case 'float':
            ws[cellRef].z = '0.00000';
            break;
          case 'percent':
            ws[cellRef].z = '0%';
            break;
          case 'percentfloat':
            ws[cellRef].z = '0.00000%';
            break;
          default:
            break;
        }
      } else if (dataType.format) {
        ws[cellRef].z = dataType.format;
      }

      if (
        typeof value === 'string' &&
        ws['!cols'][colNumber].wch < value.length
      ) {
        ws['!cols'][colNumber].wch = value.length;
      }

      colNumber += 1;
      if (maxColNumber < colNumber) maxColNumber = colNumber;
    });
  });

  // Add range to sheet
  ws['!ref'] = `A1:${getMaxColRange(maxColNumber)}${
    // the +1 here is due to the range being "Header Line" + data.length
    data.length + 1
  }`;

  wb.Sheets[sheetName || 'Sheet'] = ws;

  XLSX.writeFile(wb, `${filename}.${outputType}` || `export.${outputType}`);
};

export const useExportData: HookExportData = ({
  filename = 'Report',
  sheetName,
}) => {
  const exportData: ExportData = React.useCallback(
    ({ data, headers, type }) => {
      const parsedFilename = `${moment().format(TIMESTAMP_FORMAT)}_${filename}`;

      xlsExporter({
        filename: parsedFilename,
        sheetName,
        headers,
        data,
        outputType: type === 'xls' ? 'xlsx' : 'csv',
      });
    },
    [filename, sheetName],
  );

  return { exportData };
};

export const useExportQueryData: HookExportQueryData = ({
  filename = 'Report',
  sheetName = 'Dados',
  variables,
  query,
  parseData,
}) => {
  const [loading, setLoading] = React.useState(false);

  // Creates the lazy query
  const [getData] = useLazyQuery(query, {
    variables,
    notifyOnNetworkStatusChange: true,
    // Generate csv when query completes
    onError: () => {
      toast.error(
        'Ocorreu uma falha ao tentar exportar os dados. Tente novamente',
      );
    },
  });

  /** Exports data without performing API query */
  const exportData: ExportData = React.useCallback(
    ({ data, headers, type }) => {
      const parsedFilename = `${moment().format(TIMESTAMP_FORMAT)}_${filename}`;

      xlsExporter({
        filename: parsedFilename,
        sheetName,
        headers,
        data,
        outputType: type === 'xls' ? 'xlsx' : 'csv',
      });
    },
    [filename, sheetName],
  );

  /** Exports data performing API query */
  const exportQueryData: ExportQueryData = React.useCallback(
    async (type, headers) => {
      setLoading(true);

      try {
        const { data: unparsedData, error } = await getData();
        if (error) return null;

        const rows = parseData(unparsedData);
        exportData({ data: rows, type, headers });

        return rows.length;
      } finally {
        setLoading(false);
      }
    },
    [getData, parseData, exportData],
  );

  return { exportData, exportQueryData, loading };
};
